/* eslint-disable */
import { useCreateAuditlog } from "../../hooks/useAuditlog";
import { useUserStore } from "../../stores/user.store";
import { useEffect, useRef, useState } from "react";
import Spinner from "../../components/Spinner/index";
import { useNavigate } from "react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { consumeReturnTo } from "../../utils/returnTo";
import { Stack, Typography } from "@mui/material";

const Callback = () => {
	const { userInfo, isLoading: isUserLoading } = useUserStore();
	const { isAuthenticated, isLoading: isAuthLoading, error } = useAuth0();
	const navigate = useNavigate();
	const createAuditlogMutation = useCreateAuditlog();
	const hasNavigated = useRef(false);
	const [waitTime, setWaitTime] = useState(0);

	const postAuditlog = async ({
		action,
		actionDetail,
		actionCode,
	}: {
		action: string;
		actionDetail: string;
		actionCode: string;
	}) => {
		await createAuditlogMutation.mutateAsync({
			appType: "WEB_BACK_OFFICE",
			module: "Authentication",
			action,
			detail: actionDetail,
			actionCode,
			option: "Sign in",
		});
	};

	// Track how long we've been waiting
	useEffect(() => {
		const interval = setInterval(() => {
			setWaitTime((prev) => prev + 1);
		}, 1000);

		return () => clearInterval(interval);
	}, []);

	// Main navigation logic
	useEffect(() => {
		// Don't do anything while Auth0 is still loading
		if (isAuthLoading) {
			console.log("[Callback] Auth0 still loading...");
			return;
		}

		// If not authenticated after Auth0 finishes loading, redirect to login
		if (!isAuthenticated && !hasNavigated.current) {
			console.log(
				"[Callback] User not authenticated, redirecting to home for login",
			);
			hasNavigated.current = true;
			navigate("/");
			return;
		}

		// If authenticated but userInfo is not loaded yet, wait a bit
		if (isAuthenticated && !userInfo && !isUserLoading && waitTime < 10) {
			console.log("[Callback] Authenticated but waiting for user info...");
			return;
		}

		// If we've waited too long without getting userInfo, redirect anyway
		if (
			isAuthenticated &&
			!userInfo &&
			waitTime >= 10 &&
			!hasNavigated.current
		) {
			console.warn(
				"[Callback] Timeout waiting for user info, redirecting to home",
			);
			hasNavigated.current = true;
			navigate("/");
			return;
		}

		// If authenticated and userInfo is available, navigate to home
		if (isAuthenticated && userInfo && !hasNavigated.current) {
			hasNavigated.current = true;

			console.log(
				"[Callback] User authenticated with user info, posting audit log and navigating",
			);

			postAuditlog({
				action: "Sign in (successfully)",
				actionDetail: "User signed in successfully",
				actionCode: "WEB_SIGN_IN_OK",
			}).catch((error) => {
				console.error("[Callback] Failed to post audit log:", error);
			});

			// SIMS-9109: land on whatever the user was trying to reach before
			// sign-in, falling back to home. This used to be a hard-coded "/",
			// which silently overwrote the destination `onRedirectCallback` had
			// already been handed — so every deep link ended on the dashboard.
			// `replace` keeps /callback?code=... out of the back button.
			navigate(consumeReturnTo() ?? "/", { replace: true });
		}
	}, [
		isAuthenticated,
		isAuthLoading,
		userInfo,
		isUserLoading,
		navigate,
		waitTime,
	]);

	// If there's an Auth0 error, navigate to home
	useEffect(() => {
		if (error && !hasNavigated.current) {
			console.error("[Callback] Auth0 error:", error);
			hasNavigated.current = true;
			navigate("/");
		}
	}, [error, navigate]);

	return (
		<div className="flex w-screen h-screen items-center justify-center">
			<Stack direction="column" spacing={2} alignItems="center" maxWidth={500}>
				<Spinner />
				<Typography variant="body1" textAlign="center" color="textSecondary">
					{waitTime > 8
						? "Authentication is taking longer than expected..."
						: isAuthLoading
							? "Verifying authentication..."
							: isAuthenticated && !userInfo
								? "Loading user information..."
								: "Completing sign in..."}
				</Typography>
				{waitTime > 5 && (
					<Typography
						variant="caption"
						textAlign="center"
						color="textSecondary"
					>
						Please wait... ({waitTime}s)
					</Typography>
				)}
			</Stack>
		</div>
	);
};

export default Callback;
